/**
 * @file ProductMixReport.tsx
 * @brief Company report — volume mix across products.
 *
 * @references
 * - GET /api/company/reports/product-mix
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { LayoutGrid, Package } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import { ProductLabelCell } from "@/features/reports-shared/productIcons";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { rowCurrency } from "@/shared/utils/reportCurrency";
import {
  formatDurationSeconds,
  formatMoneyMinor,
} from "@/shared/utils/formatters";
import { useProductMixReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { percentChange } from "@/features/dashboard/kpiCompare";
import {
  previousRange,
  previousRangeLabel,
} from "@/features/reports-shared/previousRange";
import { ReportShell } from "./ReportShell";

export function ProductMixReport() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useProductMixReport(queryRange);
  const rows = report.data ?? [];
  const totalItems = rows.reduce((s, r) => s + Number(r.item_count || 0), 0);
  // The same-length window before, for the +/-% pill (03-10-2026).
  const prevQueryRange = useMemo(() => previousRange(queryRange), [queryRange]);
  const prevReport = useProductMixReport(prevQueryRange);
  const itemsDelta = prevReport.data
    ? percentChange(
        totalItems,
        prevReport.data.reduce((s, r) => s + Number(r.item_count || 0), 0),
      )
    : undefined;

  return (
    <ReportShell
      title="Product mix"
      description="Item counts, time, and spend share across Interpretation, Meeting Intel, Notes, and Plan seats."
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "product-mix",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
      enablePdf
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Products"
              icon={LayoutGrid}
              value={String(rows.length)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Items"
              icon={Package}
              value={String(totalItems)}
              hint={previousRangeLabel(queryRange)}
              delta={itemsDelta}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Mix by product"
          description="Plan seats have no duration, so share is by invoice count / spend."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Product",
              { label: "Items", align: "right" },
              { label: "Time", align: "right" },
              { label: "Spend", align: "right" },
              { label: "Item %", align: "right" },
              { label: "Time %", align: "right" },
              { label: "Spend %", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row) => (
              <tr key={row.product} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <ProductLabelCell
                    product={row.product}
                    label={row.product_label}
                  />
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.item_count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatDurationSeconds(row.total_seconds)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatMoneyMinor(row.spend_cents, rowCurrency(row))}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.item_share_pct}%
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.time_share_pct}%
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.spend_share_pct}%
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
